'use client';

import LessonsSidebar from '@/components/sidebars/lessons-sidebar';
import { useRequireAuth } from '@/hooks/auth/common/useRequireAuth';
import useUserCourseWithLessons from '@/hooks/useUserCourseWithLessons';
import { QuizSubmit } from '@/lib/api/assessment-api';
import { checkQuizAnswer } from '@/lib/actions/check-quiz-answer';
import { QuizItem, useAssessmentData as useAssessmentStore } from '@/store/assessment-store';
import { buildAbsoluteRedirectUrl } from '@/utils/auth/common/auth-storage';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { AssessmentHeader } from './AssessmentHeader';
import { AssessmentQuestion } from './AssessmentQuestion';
import { AssessmentSidebar } from './AssessmentSidebar';
import { BSToggle, CustomPopover } from './CustomPopover';
import { QuizResults } from './QuizResults';
import { calculateQuizProgress } from './services/assessmentService';
import { convertQuizDataToQuestions } from './utils/quizDataConverter';

interface QuizProps {
  assessment_test: {
    assessment: any[];
    question_count: number;
    passing_criteria?: number;
  };
  assessmentName: string;
  courseUid: string;
  isStandaloneCourse: boolean;
  lessonUid: string;
  data: any;
  videoEntry: any;
  videoConfig?: any;
  courseUrl: string;
  progress?: any;
  /** When true, progress is still loading - show loading state to prevent questionnaire flash */
  isProgressLoading?: boolean;
  /** Inline with quiz header stats (e.g. Markdown Ask AI). */
  markdownToolbar?: React.ReactNode;
}

export default function Quiz({
  assessment_test,
  assessmentName,
  courseUid,
  isStandaloneCourse,
  lessonUid,
  data,
  videoEntry,
  videoConfig,
  courseUrl,
  progress,
  isProgressLoading = false,
  markdownToolbar,
}: QuizProps) {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [quizData, setQuizData] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [showResult, setShowResult] = useState(true); // Start as true to show results if they exist (like Academy 1.0)
  const [isQuestionCardOpen, setIsQuestionCardOpen] = useState(true);
  const [correctAnswers, setCorrectAnswers] = useState<string | string[] | boolean | null>(null);
  const [previousLessonUid, setPreviousLessonUid] = useState<string | null>(null);
  const [resultsQuestionIndex, setResultsQuestionIndex] = useState(0);
  const retakeRequestedRef = React.useRef(false);
  const [retakeInProgress, setRetakeInProgress] = useState(false);

  // Show sidebar while taking quiz, or on results when per-question answers are shown; hide on score-card-only view.
  const showQuizProgressSidebar = !result || (result && result?.results?.length > 0);

  // Reset to question 0 when entering results view
  useEffect(() => {
    if (result?.results?.length) {
      setResultsQuestionIndex(0);
    }
  }, [result]);

  const {
    quiz, // Global quiz array like Academy 1.0
    setQuiz,
    setSubmittedQuiz,
    clearQuiz,
    setQuizData: setQuizDataStore,
    clearAssessment,
    assessments,
  } = useAssessmentStore();

  const quizStoreKey = `quiz-${lessonUid}`;

  // Memoized to avoid an infinite loop from a new object reference each render
  const storedQuizData = React.useMemo(() => {
    return assessments[quizStoreKey]?.quizData || null;
  }, [assessments, quizStoreKey]);

  // Server-checked answers for submitted questions; persisted so a refresh keeps the feedback.
  const answerChecks = React.useMemo(
    () => assessments[quizStoreKey]?.answerChecks ?? {},
    [assessments, quizStoreKey]
  );

  // Use stored quiz data if present (persists across refreshes); otherwise randomize/slice questions (including after retake) and store them
  useLayoutEffect(() => {
    if (storedQuizData) {
      setQuizData(storedQuizData);
    } else if (assessment_test?.assessment) {
      const randomizedQuestions = assessment_test.assessment
    .sort(() => Math.random() - 0.5)
    .slice(0, assessment_test.question_count);

      const newQuizData = {
      ...assessment_test,
        assessment: randomizedQuestions,
      };

      setQuizDataStore(quizStoreKey, {
        quizData: newQuizData,
      });

      setQuizData(newQuizData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonUid, storedQuizData]);

  const quizQuestions = useMemo(() => {
    return quizData?.assessment || [];
  }, [quizData]);

  const questions = useMemo(() => {
    if (!quizQuestions.length) return [];
    return convertQuizDataToQuestions(quizQuestions);
  }, [quizQuestions]);

  // Scope to current lesson so refresh/multi-lesson navigation doesn't mix answers (store's quiz array is global)
  const quizArray = useMemo(() => {
    const all = Array.isArray(quiz) ? quiz : [];
    if (!questions.length) return all;
    const currentUids = new Set(questions.map((q) => q.metadata_uid));
    return all.filter((q) => currentUids.has(q.question_uid));
  }, [quiz, questions]);

  const question = questions[questionIndex] || null;
  const questionType = question ? (question.question_type ||
    (question.multiple_choice_question ? 'multiple_choice_question' :
      question.single_choice_question ? 'single_choice_question' :
        question.true_or_false_question ? 'true_or_false_question' : null)) : null;

  const quizSubmit = useMutation({
    mutationKey: ['quizSubmit', lessonUid],
    mutationFn: async (formData: any[]) =>
      QuizSubmit({
        lessonUid,
        courseUid,
        formData,
      }),
    onSuccess: (data) => {
      setResult({
        ...data,
        lastAttemptedAt: (data as any)?.lastAttemptedAt ?? new Date().toISOString(),
        attemptLog: (data as any)?.attemptLog ?? [],
      });
      setShowResult(true);
      setRetakeInProgress(false);
      BSToggle('submit-popup');
      retakeRequestedRef.current = false;
      // Invalidate cached progress so it reflects this attempt on next fetch
      queryClient.invalidateQueries({ queryKey: ['user-course-lessons', courseUid] });
      queryClient.invalidateQueries({ queryKey: ['learning-path-lessons', courseUid] });
      queryClient.invalidateQueries({ queryKey: ['user-lesson', courseUid, lessonUid] });
    },
  });

  const progressQuizData = useMemo(() => {
    const assessment =
      progress?.progress?.assessment ?? progress?.assessment;
    const attemptLog = assessment?.attemptLog;
    if (!attemptLog || !Array.isArray(attemptLog) || attemptLog.length === 0)
      return null;
    const lastAttempt = attemptLog.at(-1);
    if (!lastAttempt) return null;
    // Fall back to older result shapes so per-question display (selected/correct options) still works
    const results =
      lastAttempt.results ??
      (progress as any)?.quizResult?.results ??
      lastAttempt.quizResult?.results;
    return {
      attemptLog,
      lastAttempt,
      results,
    };
  }, [progress]);

  // Restore last result from fetched progress on return/refresh; skip while a retake is in progress so we don't clobber the fresh attempt
  useEffect(() => {
    if (retakeRequestedRef.current || retakeInProgress) {
      return;
    }
    if (!progressQuizData) return;
    const { attemptLog, lastAttempt, results } = progressQuizData;
    setResult({
      ...lastAttempt,
      totalMarks: lastAttempt.totalMarks ?? 0,
      totalQuestions: lastAttempt.totalQuestions ?? 0,
      percentageAchieved: lastAttempt.percentageAchieved ?? 0,
      attemptNumber: lastAttempt.attemptNumber,
      attemptedAt: lastAttempt.attemptedAt,
      results: results ?? lastAttempt.results,
      attemptLog,
    });
    setShowResult(true);
  }, [progressQuizData, retakeInProgress]);

  // Sole auth gate for the quiz flow (quiz-client-wrapper.tsx has none of its own). authLoading is
  // real round-trip time, so it extends the loading skeleton to prevent a pre-redirect content flash.
  const { isLoading: authLoading } = useRequireAuth('/?openLogin=1');

  // Clear quiz state only when navigating to a different lesson
  useEffect(() => {
    if (previousLessonUid !== null && previousLessonUid !== lessonUid) {
      clearQuiz();
      setQuestionIndex(0);
      setResult(null);
      setCorrectAnswers(null);
      setShowResult(true);
      setRetakeInProgress(false);
      retakeRequestedRef.current = false;
    }
    setPreviousLessonUid(lessonUid);
  }, [lessonUid, previousLessonUid, clearQuiz]);

  // Compute correctness highlighting for the current question once it's submitted
  useEffect(() => {
    if (!quizData) {
      setCorrectAnswers(null);
      return;
    }

    const currentQuizItem = quizArray.find((q) => q.qindex === questionIndex);

    if (currentQuizItem?.isSubmitted) {
      const currentQuestionData = quizData.assessment?.[questionIndex];
      const questionUid = questions[questionIndex]?.metadata_uid;
      if (currentQuestionData && questionUid && questionUid in answerChecks) {
        const correctOptions = answerChecks[questionUid];
        const questionTypeKey = Object.keys(currentQuestionData)[0];

        let isInCorrect = false;
        if (questionTypeKey === 'multiple_choice_questions') {
          const correctArray = Array.isArray(correctOptions) ? correctOptions : [];
          const selectedArray = Array.isArray(currentQuizItem?.selected_options)
            ? currentQuizItem.selected_options
            : [];
          isInCorrect = JSON.stringify([...correctArray].sort()) !== JSON.stringify([...selectedArray].sort());
        } else {
          isInCorrect = correctOptions !== currentQuizItem?.selected_options;
        }

        if (isInCorrect) {
          if (questionTypeKey === 'multiple_choice_questions' && Array.isArray(correctOptions)) {
            setCorrectAnswers(correctOptions.join(', '));
          } else if (correctOptions != null) {
            setCorrectAnswers(correctOptions);
          } else {
            setCorrectAnswers(null);
          }
        } else {
          setCorrectAnswers(null);
        }
      } else {
        setCorrectAnswers(null);
      }
    } else {
      setCorrectAnswers(null);
    }

    // No auto-submit - user must click "Submit Quiz" and confirm via popup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz, questionIndex, quizData, result, answerChecks, questions]); // quizSubmit excluded from deps to avoid an infinite loop

  // The answer key never reaches the browser, so ask the server. Resolves false if unchecked.
  const fetchAnswerCheck = React.useCallback(
    async (questionUid: string): Promise<boolean> => {
      const check = await checkQuizAnswer(courseUid, lessonUid, questionUid);
      if (!check.ok) return false;
      // Read from the store, not this render's copy, so quick submits don't overwrite each other.
      const latest = useAssessmentStore.getState().assessments[quizStoreKey]?.answerChecks ?? {};
      setQuizDataStore(quizStoreKey, { answerChecks: { ...latest, [questionUid]: check.correctAnswer } });
      return true;
    },
    [courseUid, lessonUid, quizStoreKey, setQuizDataStore]
  );

  // Submit only after the check: option components show a submitted question with no answer as correct.
  const checkingAnswerRef = React.useRef(false);
  const handleQuestionSubmit = async (index: number) => {
    const questionUid = questions[index]?.metadata_uid;
    if (!questionUid || checkingAnswerRef.current) return;
    checkingAnswerRef.current = true;
    try {
      if (await fetchAnswerCheck(questionUid)) {
        setSubmittedQuiz(index);
      } else {
        alert("We couldn't check your answer. Please try again.");
      }
    } finally {
      checkingAnswerRef.current = false;
    }
  };

  // State persisted before server-side checks has no stored answer — fetch it.
  useEffect(() => {
    const questionUid = questions[questionIndex]?.metadata_uid;
    const isSubmitted = quizArray.find((q) => q.qindex === questionIndex)?.isSubmitted;
    if (isSubmitted && questionUid && !(questionUid in answerChecks)) {
      void fetchAnswerCheck(questionUid);
    }
  }, [questionIndex, questions, quizArray, answerChecks, fetchAnswerCheck]);

  const quizProgressPercentage = calculateQuizProgress(
    quizArray.length,
    questions.length
  );

  const { userCourseData, isLoading: isLoadingCourseData } = useUserCourseWithLessons({
    courseEntryUid: courseUid,
    lessons: data?.chapters?.[0]?.lessons?.lessons || [],
    lessonEntryUid: lessonUid,
  });

  const handleSetQuizData = (uid: string, data: QuizItem[]) => {
    data.forEach((quizItem) => {
      setQuiz(quizStoreKey, quizItem);
    });
  };

  const isNormalCourseWithoutLearningPath = pathname.includes('/courses');
  // Derive the path segment AssessmentHeader/QuizResults expect (prefixable with /learning-paths) from
  // courseUrl's format: /learning-paths/{learningPath}/{courseId} or /courses/{courseId}
  const { learningPathUrl, learningPath } = useMemo(() => {
    let url = courseUrl;
    let path: string | undefined;
    if (courseUrl.includes('/learning-paths')) {
      url = courseUrl.replace(/^\/learning-paths/, '');
      const segments = url.split('/').filter(Boolean);
      path = segments[0];
    } else if (courseUrl.includes('/courses')) {
      url = courseUrl.replace(/^\/courses/, '');
      path = undefined;
    }
    return { learningPathUrl: url, learningPath: path };
  }, [courseUrl]);

  // Next chapter: go to next lesson in course or learning path; or back to course/LP when on last lesson
  const { nextChapterHandler, nextChapterLabel } = useMemo(() => {
    const lessonsArray =
      data?.chapters?.[0]?.lessons?.lessons ??
      (Array.isArray(data?.chapters?.[0]?.lessons) ? data.chapters[0].lessons : null);

    const pathSegments = pathname.split('/').filter(Boolean);
    const currentSlugFromPath = pathSegments[pathSegments.length - 1] || '';
    const currentLessonUrlSlug =
      videoEntry?.url?.replace(/^\//, '').split('/').pop() ||
      videoEntry?.url?.replace(/^\//, '') ||
      currentSlugFromPath;

    const normalizeSlug = (urlOrSlug: string | undefined) =>
      urlOrSlug?.replace(/^\//, '').split('/').pop() || urlOrSlug?.replace(/^\//, '') || '';

    const buildNextUrl = (lessonSlug: string) => {
      const slug = lessonSlug?.replace(/^\//, '').split('/').pop() || lessonSlug?.replace(/^\//, '') || lessonSlug;
      if (learningPath) {
        return `/learning-paths${learningPathUrl}/${slug}`;
      }
      return `/courses${learningPathUrl}/${slug}`;
    };

    const navigateToCourseOrLp = () => {
      const base = learningPath ? `/learning-paths${learningPathUrl}` : `/courses${learningPathUrl}`;
      router.push(base.replace(/\/$/, ''));
    };

    const backLabel = learningPath ? 'Back to learning path' : 'Back to course';

    if (lessonsArray?.length) {
      const currentLessonIndex = lessonsArray.findIndex((lesson: any) => {
        const slug = normalizeSlug(lesson?.url);
        return (
          lesson?.uid === lessonUid ||
          lesson?.entryUid === lessonUid ||
          (slug && (slug === currentLessonUrlSlug || slug === currentSlugFromPath))
        );
      });

      if (currentLessonIndex !== -1 && currentLessonIndex < lessonsArray.length - 1) {
        const nextLesson = lessonsArray[currentLessonIndex + 1];
        const nextSlug = normalizeSlug(nextLesson?.url) || nextLesson?.url;
        if (nextSlug) {
          return {
            nextChapterHandler: () => router.push(buildNextUrl(nextSlug)),
            nextChapterLabel: 'Next chapter' as const,
          };
        }
      }
      return { nextChapterHandler: navigateToCourseOrLp, nextChapterLabel: backLabel };
    }

    if (videoConfig && typeof videoConfig === 'object' && Object.keys(videoConfig).length > 0) {
      const configEntries = Object.entries(videoConfig) as [string, { url?: string; title?: string }][];
      const currentIndex = configEntries.findIndex(([, config]) => {
        const slug = normalizeSlug(config?.url);
        return slug === currentLessonUrlSlug || slug === currentSlugFromPath;
      });

      if (currentIndex !== -1 && currentIndex < configEntries.length - 1) {
        const [, nextConfig] = configEntries[currentIndex + 1];
        const nextSlug = normalizeSlug(nextConfig?.url);
        if (nextSlug) {
          return {
            nextChapterHandler: () => router.push(buildNextUrl(nextSlug)),
            nextChapterLabel: 'Next chapter' as const,
          };
        }
      }
      return { nextChapterHandler: navigateToCourseOrLp, nextChapterLabel: backLabel };
    }

    return { nextChapterHandler: navigateToCourseOrLp, nextChapterLabel: backLabel };
  }, [data, lessonUid, learningPath, learningPathUrl, router, videoConfig, videoEntry, pathname]);

  // Build results from result.results (API) so answers show on revisit; match by question UID only,
  // never by index, so randomized question order doesn't mix up answers
  const quizForResults = useMemo(() => {
    if (!result?.results?.length) return quizArray;
    const apiResults = result.results as Array<{
      questionUid?: string;
      question_uid?: string;
      selectedOptions?: string | string[] | boolean;
      selected_options?: string | string[] | boolean;
    }>;
    const fromStore = quizArray;

    return questions.map((q, i) => {
      const apiResult = apiResults.find(
        (r) => (r.questionUid ?? r.question_uid) === q.metadata_uid
      );
      const storeItem = fromStore.find((qi) => qi.question_uid === q.metadata_uid);
      let selectedOpt: string | string[] | boolean | undefined =
        apiResult?.selectedOptions ?? apiResult?.selected_options ?? storeItem?.selected_options;

      if (selectedOpt === undefined) {
        return {
          question_uid: q.metadata_uid,
          selected_options: q.multiple_choice_question != null ? [] : (q.single_choice_question != null ? '' : false),
          qindex: i,
          isSubmitted: !!storeItem?.isSubmitted,
        };
      }

      // Normalize by question type so checkboxes/radios show correctly
      if (q.true_or_false_question != null) {
        if (typeof selectedOpt === 'string') selectedOpt = selectedOpt === 'true';
      } else if (q.multiple_choice_question != null) {
        if (!Array.isArray(selectedOpt)) selectedOpt = [selectedOpt as string];
      } else if (q.single_choice_question != null) {
        if (Array.isArray(selectedOpt) && selectedOpt.length === 1) selectedOpt = selectedOpt[0] as string;
        else if (Array.isArray(selectedOpt) && selectedOpt.length > 1) selectedOpt = selectedOpt[0] as string;
      }

      return {
        question_uid: q.metadata_uid,
        selected_options: selectedOpt,
        qindex: i,
        isSubmitted: true,
      };
    });
  }, [result, quizArray, questions]);

  return (
    <>
      <div className="mx-auto w-full">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content Area */}
      <div
        id="quiz"
              className="flex-1 flex flex-col"
            >
              {(isProgressLoading || authLoading) && !result && (
                <div className="flex flex-col gap-4 py-8">
                  <div className="h-8 w-48 bg-surface-4 rounded animate-pulse" />
                  <div className="h-64 w-full bg-surface-4 rounded animate-pulse" />
                </div>
              )}
              {!isProgressLoading && !authLoading && !result && (
                <>
                  <AssessmentHeader
                    assessmentName={assessmentName}
                    learningPathUrl={learningPathUrl}
                    updatedAt={videoEntry?.updated_at || videoEntry?.updatedAt || new Date().toISOString()}
                    totalQuestions={questions.length}
                    questionIndex={questionIndex}
                    isStandaloneCourse={isStandaloneCourse}
                    type="quiz"
                    onToggleQuestionCard={() => setIsQuestionCardOpen(!isQuestionCardOpen)}
                    isQuestionCardOpen={isQuestionCardOpen}
                    markdownToolbar={markdownToolbar}
                  />
                  {question && questionType && (
                    <AssessmentQuestion
                      question={question}
                      questionType={questionType}
                      questionIndex={questionIndex}
                      totalQuestions={questions.length}
                      quiz={quizArray}
                      learningPathwayUID={quizStoreKey}
                      setQuizData={handleSetQuizData}
                      submitButtonText="Submit Quiz"
                      isQuestionCardOpen={isQuestionCardOpen}
                      isQuiz={true}
                      correctAnswers={correctAnswers}
                      quizData={quizData}
                      onQuestionSubmit={() => handleQuestionSubmit(questionIndex)}
                      onRequestSubmitQuiz={() => {
                        if (typeof window !== 'undefined') {
                          window.dispatchEvent(new CustomEvent('toggle-popover', { detail: { id: 'submit-popup' } }));
                        }
                      }}
                      onNext={() => {
                        if (questionIndex < questions.length - 1) {
                          setQuestionIndex(questionIndex + 1);
                        }
                      }}
                      onPrevious={() => {
                        if (questionIndex > 0) {
                          setQuestionIndex(questionIndex - 1);
                        }
                      }}
                    />
                        )}
                      </>
                    )}

              {/* Results */}
              {result && showResult && (
                <QuizResults
                  result={result}
                  assessment={{
                    passing_criteria: assessment_test?.passing_criteria || 75,
                    question_count: questions.length,
                  }}
                  assessmentName={assessmentName}
                  learningPathUrl={learningPathUrl}
                  updatedAt={videoEntry?.updated_at || videoEntry?.updatedAt || new Date().toISOString()}
                  totalQuestions={questions.length}
                  questions={questions}
                  quiz={quizForResults}
                  quizData={quizData}
                  courseProgressPercentage={0}
                  isStandaloneCourse={isStandaloneCourse}
                  scoreCardOnly={!(result?.results?.length)}
                  completedAt={result?.lastAttemptedAt ?? (result as any)?.attemptLog?.at(-1)?.attemptedAt ?? undefined}
                  controlledQuestionIndex={result?.results?.length ? resultsQuestionIndex : undefined}
                  onQuestionIndexChange={result?.results?.length ? setResultsQuestionIndex : undefined}
                  onRetake={() => {
                    retakeRequestedRef.current = true;
                    setRetakeInProgress(true);
                    setShowResult(false);
                    setResult(null);
                    clearQuiz();
                    setQuestionIndex(0);
                    setCorrectAnswers(null);
                    clearAssessment(quizStoreKey);
                    setQuizData(null);
                  }}
                  onNextChapter={nextChapterHandler}
                  nextChapterLabel={nextChapterLabel}
                />
              )}

              {/* Sidebar Below Content - Mobile & Tablet: show when taking quiz or when results show question+answer */}
              {showQuizProgressSidebar && (
                <div className="lg:hidden mt-6 w-full md:w-1/2">
                  <div className="flex flex-col gap-6">
                    {/* Course Sidebar - only when taking quiz, not on results */}
                    {!result && data?.chapters?.[0]?.lessons && (
                      <div className="w-full">
                        <LessonsSidebar
                          courseUid={courseUid}
                          courseUrl={courseUrl}
                          lessonUrl={videoEntry?.url || ''}
                          lessons={data.chapters[0].lessons}
                          eyebrow="Course"
                          heading={data?.title || assessmentName}
                          defaultExpanded={false}
                          userCourseData={userCourseData}
                          isLoading={isLoadingCourseData}
                          learningPath={learningPath}
                          showAssessment={false}
                          isVideoPlaying={false}
                          currentLessonUid={lessonUid}
                        />
                      </div>
                    )}

                    {/* Quiz Progress Section */}
                    <div className="w-full">
                      <AssessmentSidebar
                        courseProgressPercentage={0}
                        quizProgressPercentage={result ? 100 : quizProgressPercentage}
                        questions={questions}
                        questionIndex={result ? resultsQuestionIndex : questionIndex}
                        quiz={result ? quizForResults : quizArray}
                        result={result}
                        onQuestionClick={result ? setResultsQuestionIndex : setQuestionIndex}
                        type="quiz"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Right Side - Desktop Only: show when taking quiz or when results show question+answer */}
            {showQuizProgressSidebar && (
              <div className="hidden lg:block lg:w-[30%] lg:sticky lg:top-20 lg:self-start">
                <div className="flex flex-col gap-8">
                  {/* Course Sidebar - only when taking quiz, not on results */}
                  {!result && data?.chapters?.[0]?.lessons && (
                    <LessonsSidebar
                      courseUid={courseUid}
                      courseUrl={courseUrl}
                      lessonUrl={videoEntry?.url || ''}
                      lessons={data.chapters[0].lessons}
                      eyebrow="Course"
                      heading={data?.title || assessmentName}
                      defaultExpanded={false}
                      userCourseData={userCourseData}
                      isLoading={isLoadingCourseData}
                      learningPath={learningPath}
                      showAssessment={false}
                      isVideoPlaying={false}
                      currentLessonUid={lessonUid}
                    />
                  )}

                  {/* Quiz Progress Section */}
                  <AssessmentSidebar
                    courseProgressPercentage={0}
                    quizProgressPercentage={result ? 100 : quizProgressPercentage}
                    questions={questions}
                    questionIndex={result ? resultsQuestionIndex : questionIndex}
                    quiz={result ? quizForResults : quizArray}
                    result={result}
                    onQuestionClick={result ? setResultsQuestionIndex : setQuestionIndex}
                    type="quiz"
                  />
                </div>
              </div>
            )}
      </div>
        </div>

      {/* Submit Popup - uncontrolled, opened via toggle-popover event from Submit Quiz button */}
      <CustomPopover
        id="submit-popup"
        header="Submit quiz?"
        body={
          quizArray.length < questions.length
            ? `You are about to submit your answers for the <strong>${assessmentName}</strong> quiz.<br/><br/>There are still <strong>${questions.length - quizArray.length}</strong> unanswered questions. Would you like to go back to answer them or submit the quiz?`
            : `You are about to submit your answers for the <strong>${assessmentName}</strong> quiz.`
        }
        footerBtns={[
          {
            label: quizSubmit.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Submitting...
              </>
            ) : (
              'See results'
            ),
            isToggle: false,
            id: 'submit-popup',
            className: 'cs-button flex items-center gap-2',
            disabled: quizSubmit.isPending,
            onClick: async () => {
              try {
                // Build formData from quiz array, dropping items with no/invalid answer
                const formData = quizArray
                  .map((quizItem) => {
                    const selectedOptions = quizItem.selected_options;

                    if (selectedOptions === null || selectedOptions === undefined) {
                      return null;
                    }

                    const isValidOptions =
                      typeof selectedOptions === 'string' ||
                      typeof selectedOptions === 'boolean' ||
                      (Array.isArray(selectedOptions) && selectedOptions.length > 0 && selectedOptions.every(opt => typeof opt === 'string'));

                    if (!isValidOptions) {
                      return null;
                    }

                    if (!quizItem.question_uid) {
                      return null;
                    }

                    return {
                      question_uid: quizItem.question_uid,
                      selected_options: selectedOptions,
                    };
                  })
                  .filter((item): item is { question_uid: string; selected_options: string | string[] | boolean } => item !== null);

                if (formData.length === 0) {
                  alert('Please answer at least one question before submitting.');
                  return;
                }

                await quizSubmit.mutateAsync(formData);
                // Popup closes via onSuccess
              } catch (error: any) {
                console.error('Failed to submit quiz:', error);
                const errorMessage = error?.response?.data?.message || error?.message || 'Failed to submit quiz. Please try again.';

                if (errorMessage.includes('Learning Material not completed') || errorMessage.includes('Learning material not completed')) {
                  alert('Please complete the lesson content before submitting the quiz. Make sure you have watched/read all the learning material.');
                } else {
                  alert(errorMessage);
                }
                
                // Keep popup open on error so user can retry
              }
            },
          },
        ]}
      />

      {/* Leave Popup */}
      <CustomPopover
        id="leave-popup"
        header="Leave Quiz"
        body={`${questions.length - quizArray.length > 0 && !result
            ? `You have <strong>${questions.length - quizArray.length}</strong> unanswered questions, but don't worry - your progress is saved. You can exit and return anytime to complete your quiz.`
            : ''
          } Are you sure you want to leave?`}
        footerBtns={[
          {
            label: 'Go Back',
            isToggle: true,
            id: 'leave-popup',
            className: 'admy-btn-hover-underline border-0',
          },
          {
            label: 'Yes, Leave',
            isToggle: true,
            id: 'leave-popup',
            onClick: () => {
              if (pathname.includes('learning-paths')) {
                window.location.href = buildAbsoluteRedirectUrl(courseUrl);
              } else if (pathname.includes('courses')) {
                window.location.href = buildAbsoluteRedirectUrl(`/courses${courseUrl}`);
              }
            },
          },
        ]}
      />
    </>
  );
}
